<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  results: { type: Array, default: () => [] },
  criteria: { type: Array, default: () => [] },
});

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const headers = computed(() => [
  t('ANALYSIS.QUALITY.RESULTS.CONVERSATION'),
  t('ANALYSIS.QUALITY.RESULTS.INBOX'),
  t('ANALYSIS.QUALITY.RESULTS.AGENT'),
  t('ANALYSIS.QUALITY.RESULTS.SCORE'),
  t('ANALYSIS.QUALITY.RESULTS.EVIDENCE'),
  t('ANALYSIS.QUALITY.RESULTS.DATE'),
]);

// The one free-text criterion is what a reader scans the table by; the scores are the summary's job.
const summaryKey = computed(
  () => props.criteria.find(criterion => criterion.kind === 'text')?.key
);

const linkTo = result =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      id: result.conversation.display_id,
    })
  );

const scoreColor = score => {
  if (score === null || score === undefined) return 'text-n-slate-11';
  if (score < 3) return 'text-n-ruby-11';
  if (score < 4) return 'text-n-amber-11';
  return 'text-n-teal-11';
};

const firstEvidence = result => Object.values(result.evidence || {})[0];
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-0.5">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ANALYSIS.QUALITY.RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('ANALYSIS.QUALITY.RESULTS.SUBTITLE') }}
      </p>
    </div>
    <BaseTable :headers="headers" :items="results">
      <template #row="{ items }">
        <BaseTableRow v-for="result in items" :key="result.id" :item="result">
          <BaseTableCell>
            <router-link
              :to="linkTo(result)"
              class="flex flex-col min-w-0 group"
            >
              <span class="font-medium text-n-slate-12 group-hover:underline">
                {{
                  `#${result.conversation.display_id} ${result.conversation.contact_name ?? ''}`
                }}
              </span>
              <span
                v-if="summaryKey && result.results[summaryKey]"
                class="text-n-slate-11 line-clamp-1"
              >
                {{ result.results[summaryKey] }}
              </span>
            </router-link>
          </BaseTableCell>
          <BaseTableCell>
            <span class="text-n-slate-11">{{
              result.conversation.inbox_name
            }}</span>
          </BaseTableCell>
          <BaseTableCell>
            <span class="text-n-slate-11">
              {{ result.conversation.assignee_name || '—' }}
            </span>
          </BaseTableCell>
          <BaseTableCell>
            <span
              class="font-medium tabular-nums"
              :class="scoreColor(result.composite_score)"
            >
              {{ result.composite_score ?? '—' }}
            </span>
          </BaseTableCell>
          <BaseTableCell>
            <span
              class="text-n-slate-11 line-clamp-1 italic"
              :title="t('ANALYSIS.QUALITY.RESULTS.EVIDENCE_HINT')"
            >
              {{ firstEvidence(result) || '—' }}
            </span>
          </BaseTableCell>
          <BaseTableCell>
            <span class="text-n-slate-11 tabular-nums">
              {{ new Date(result.evaluated_at).toLocaleDateString() }}
            </span>
          </BaseTableCell>
        </BaseTableRow>
      </template>
    </BaseTable>
  </div>
</template>
